/**
 * Runtime Abstraction Layer Generator
 * Generates engine-independent C# runtime files for logging, JSON, and storage
 * These files allow the generated database code to work in Unity, Godot, or standalone .NET
 */

/**
 * Generate GrimoireLog.cs - Logging abstraction with delegate-based sinks
 * @param namespace - C# namespace (default: 'Grimoire.Data.Runtime')
 */
export function renderGrimoireLogFile(namespace: string = 'Grimoire.Data.Runtime'): string {
  let output = '// Grimoire Runtime - Logging Abstraction\n';
  output += '// This file provides engine-independent logging.\n';
  output += '// Configure the sinks to route logs to your engine (Unity, Godot, etc.)\n\n';
  output += 'using System;\n\n';
  output += `namespace ${namespace}\n{\n`;
  output += '\t/// <summary>\n';
  output += '\t/// Engine-independent logging abstraction.\n';
  output += '\t/// Set InfoSink, WarnSink, and ErrorSink to route logs to your engine.\n';
  output += '\t/// </summary>\n';
  output += '\tpublic static class GrimoireLog\n\t{\n';
  output += '\t\t/// <summary>Sink for info-level messages. Default: Console.WriteLine</summary>\n';
  output += '\t\tpublic static Action<string> InfoSink = msg => Console.WriteLine($"[Info] {msg}");\n\n';
  output += '\t\t/// <summary>Sink for warning-level messages. Default: Console.WriteLine</summary>\n';
  output += '\t\tpublic static Action<string> WarnSink = msg => Console.WriteLine($"[Warn] {msg}");\n\n';
  output += '\t\t/// <summary>Sink for error-level messages. Default: Console.Error.WriteLine</summary>\n';
  output += '\t\tpublic static Action<string> ErrorSink = msg => Console.Error.WriteLine($"[Error] {msg}");\n\n';
  output += '\t\t/// <summary>Log an info-level message</summary>\n';
  output += '\t\tpublic static void Info(string msg) => InfoSink?.Invoke(msg);\n\n';
  output += '\t\t/// <summary>Log a warning-level message</summary>\n';
  output += '\t\tpublic static void Warn(string msg) => WarnSink?.Invoke(msg);\n\n';
  output += '\t\t/// <summary>Log an error-level message</summary>\n';
  output += '\t\tpublic static void Error(string msg) => ErrorSink?.Invoke(msg);\n';
  output += '\t}\n';
  output += '}\n';

  return output;
}

/**
 * Generate GrimoireJson.cs - JSON serialization abstraction
 * Uses Newtonsoft.Json for Unity, System.Text.Json for other platforms
 * @param namespace - C# namespace (default: 'Grimoire.Data.Runtime')
 */
export function renderGrimoireJsonFile(namespace: string = 'Grimoire.Data.Runtime'): string {
  let output = '// Grimoire Runtime - JSON Serialization Abstraction\n';
  output += '// This file provides engine-independent JSON serialization.\n';
  output += '// For Unity: Uses Newtonsoft.Json (install via Package Manager: com.unity.nuget.newtonsoft-json)\n';
  output += '// For Godot/.NET: Uses System.Text.Json or configure your preferred library.\n\n';
  output += 'using System;\n';
  output += '#if UNITY_5_3_OR_NEWER\n';
  output += 'using Newtonsoft.Json;\n';
  output += '#elif !NETSTANDARD2_0\n';
  output += 'using System.Text.Json;\n';
  output += '#endif\n\n';
  output += `namespace ${namespace}\n{\n`;
  output += '\t/// <summary>\n';
  output += '\t/// Engine-independent JSON serialization abstraction.\n';
  output += '\t/// Unity: Uses Newtonsoft.Json (install via Package Manager: com.unity.nuget.newtonsoft-json)\n';
  output += '\t/// Godot/.NET: Uses System.Text.Json or set ToJsonImpl/FromJsonImpl for custom library.\n';
  output += '\t/// </summary>\n';
  output += '\tpublic static class GrimoireJson\n\t{\n';
  output += '\t\t/// <summary>Implementation for serializing objects to JSON</summary>\n';
  output += '\t\tpublic static Func<object, bool, string> ToJsonImpl = DefaultToJson;\n\n';
  output += '\t\t/// <summary>Implementation for deserializing JSON to objects</summary>\n';
  output += '\t\tpublic static Func<string, Type, object> FromJsonImpl = DefaultFromJson;\n\n';
  output += '\t\t/// <summary>Serialize an object to JSON string</summary>\n';
  output += '\t\t/// <typeparam name="T">Type of object to serialize</typeparam>\n';
  output += '\t\t/// <param name="obj">Object to serialize</param>\n';
  output += '\t\t/// <param name="pretty">Whether to use indented formatting</param>\n';
  output += '\t\t/// <returns>JSON string representation</returns>\n';
  output += '\t\tpublic static string ToJson<T>(T obj, bool pretty = false)\n\t\t{\n';
  output += '\t\t\treturn ToJsonImpl(obj, pretty);\n';
  output += '\t\t}\n\n';
  output += '\t\t/// <summary>Deserialize a JSON string to an object</summary>\n';
  output += '\t\t/// <typeparam name="T">Type to deserialize to</typeparam>\n';
  output += '\t\t/// <param name="json">JSON string to deserialize</param>\n';
  output += '\t\t/// <returns>Deserialized object</returns>\n';
  output += '\t\tpublic static T FromJson<T>(string json)\n\t\t{\n';
  output += '\t\t\treturn (T)FromJsonImpl(json, typeof(T));\n';
  output += '\t\t}\n\n';
  output += '\t\t#region Default Implementation\n\n';
  output += '#if UNITY_5_3_OR_NEWER\n';
  output += '\t\t// Unity: Use Newtonsoft.Json (install via Package Manager: com.unity.nuget.newtonsoft-json)\n';
  output += '\t\tprivate static string DefaultToJson(object obj, bool pretty)\n\t\t{\n';
  output += '\t\t\tvar settings = new JsonSerializerSettings\n\t\t\t{\n';
  output += '\t\t\t\tFormatting = pretty ? Formatting.Indented : Formatting.None,\n';
  output += '\t\t\t\tReferenceLoopHandling = ReferenceLoopHandling.Ignore\n';
  output += '\t\t\t};\n';
  output += '\t\t\treturn JsonConvert.SerializeObject(obj, settings);\n';
  output += '\t\t}\n\n';
  output += '\t\tprivate static object DefaultFromJson(string json, Type type)\n\t\t{\n';
  output += '\t\t\treturn JsonConvert.DeserializeObject(json, type);\n';
  output += '\t\t}\n';
  output += '#elif NETSTANDARD2_0\n';
  output += '\t\t// .NET Standard 2.0 fallback - requires manual configuration\n';
  output += '\t\tprivate static string DefaultToJson(object obj, bool pretty)\n\t\t{\n';
  output += '\t\t\tthrow new NotSupportedException(\n';
  output += '\t\t\t\t"GrimoireJson requires configuration on .NET Standard 2.0. " +\n';
  output += '\t\t\t\t"Set GrimoireJson.ToJsonImpl to use your preferred JSON library.");\n';
  output += '\t\t}\n\n';
  output += '\t\tprivate static object DefaultFromJson(string json, Type type)\n\t\t{\n';
  output += '\t\t\tthrow new NotSupportedException(\n';
  output += '\t\t\t\t"GrimoireJson requires configuration on .NET Standard 2.0. " +\n';
  output += '\t\t\t\t"Set GrimoireJson.FromJsonImpl to use your preferred JSON library.");\n';
  output += '\t\t}\n';
  output += '#else\n';
  output += '\t\t// .NET Standard 2.1+ / .NET Core 3.0+ / Godot - use System.Text.Json\n';
  output += '\t\tprivate static string DefaultToJson(object obj, bool pretty)\n\t\t{\n';
  output += '\t\t\tvar options = new JsonSerializerOptions\n\t\t\t{\n';
  output += '\t\t\t\tWriteIndented = pretty,\n';
  output += '\t\t\t\tPropertyNamingPolicy = JsonNamingPolicy.CamelCase\n';
  output += '\t\t\t};\n';
  output += '\t\t\treturn JsonSerializer.Serialize(obj, options);\n';
  output += '\t\t}\n\n';
  output += '\t\tprivate static object DefaultFromJson(string json, Type type)\n\t\t{\n';
  output += '\t\t\tvar options = new JsonSerializerOptions\n\t\t\t{\n';
  output += '\t\t\t\tPropertyNamingPolicy = JsonNamingPolicy.CamelCase,\n';
  output += '\t\t\t\tPropertyNameCaseInsensitive = true\n';
  output += '\t\t\t};\n';
  output += '\t\t\treturn JsonSerializer.Deserialize(json, type, options);\n';
  output += '\t\t}\n';
  output += '#endif\n\n';
  output += '\t\t#endregion\n';
  output += '\t}\n';
  output += '}\n';

  return output;
}

/**
 * Generate GrimoireStorage.cs - Persistent storage abstraction (PlayerPrefs replacement)
 * Default implementation uses file-based storage in LocalApplicationData
 * @param namespace - C# namespace (default: 'Grimoire.Data.Runtime')
 */
export function renderGrimoireStorageFile(namespace: string = 'Grimoire.Data.Runtime'): string {
  let output = '// Grimoire Runtime - Persistent Storage Abstraction\n';
  output += '// This file provides engine-independent persistent storage.\n';
  output += '// Configure the delegates to use your engine\'s storage (PlayerPrefs, Godot user://, etc.)\n\n';
  output += 'using System;\n';
  output += 'using System.Collections.Generic;\n';
  output += 'using System.IO;\n\n';
  output += `namespace ${namespace}\n{\n`;
  output += '\t/// <summary>\n';
  output += '\t/// Engine-independent persistent storage abstraction.\n';
  output += '\t/// Replaces Unity\'s PlayerPrefs with a configurable backend.\n';
  output += '\t/// Default: File-based storage in LocalApplicationData/GrimoireData/\n';
  output += '\t/// </summary>\n';
  output += '\tpublic static class GrimoireStorage\n\t{\n';
  output += '\t\t#region Delegate Configuration\n\n';
  output += '\t\t/// <summary>Implementation for checking if a key exists</summary>\n';
  output += '\t\tpublic static Func<string, bool> HasKeyImpl = DefaultHasKey;\n\n';
  output += '\t\t/// <summary>Implementation for getting a string value</summary>\n';
  output += '\t\tpublic static Func<string, string> GetStringImpl = DefaultGetString;\n\n';
  output += '\t\t/// <summary>Implementation for setting a string value</summary>\n';
  output += '\t\tpublic static Action<string, string> SetStringImpl = DefaultSetString;\n\n';
  output += '\t\t/// <summary>Implementation for saving all pending changes</summary>\n';
  output += '\t\tpublic static Action SaveImpl = DefaultSave;\n\n';
  output += '\t\t/// <summary>Implementation for deleting a key</summary>\n';
  output += '\t\tpublic static Action<string> DeleteKeyImpl = DefaultDeleteKey;\n\n';
  output += '\t\t/// <summary>Implementation for deleting all data</summary>\n';
  output += '\t\tpublic static Action DeleteAllImpl = DefaultDeleteAll;\n\n';
  output += '\t\t#endregion\n\n';
  output += '\t\t#region Public API\n\n';
  output += '\t\t/// <summary>Check if a key exists in storage</summary>\n';
  output += '\t\tpublic static bool HasKey(string key) => HasKeyImpl(key);\n\n';
  output += '\t\t/// <summary>Get a string value from storage</summary>\n';
  output += '\t\tpublic static string GetString(string key) => GetStringImpl(key);\n\n';
  output += '\t\t/// <summary>Get a string value with default if not found</summary>\n';
  output += '\t\tpublic static string GetString(string key, string defaultValue)\n\t\t{\n';
  output += '\t\t\treturn HasKey(key) ? GetStringImpl(key) : defaultValue;\n';
  output += '\t\t}\n\n';
  output += '\t\t/// <summary>Set a string value in storage</summary>\n';
  output += '\t\tpublic static void SetString(string key, string value) => SetStringImpl(key, value);\n\n';
  output += '\t\t/// <summary>Save all pending changes to persistent storage</summary>\n';
  output += '\t\tpublic static void Save() => SaveImpl();\n\n';
  output += '\t\t/// <summary>Delete a key from storage</summary>\n';
  output += '\t\tpublic static void DeleteKey(string key) => DeleteKeyImpl(key);\n\n';
  output += '\t\t/// <summary>Delete all data from storage</summary>\n';
  output += '\t\tpublic static void DeleteAll() => DeleteAllImpl();\n\n';
  output += '\t\t#endregion\n\n';
  output += '\t\t#region Default File-Based Implementation\n\n';
  output += '\t\tprivate static Dictionary<string, string> _cache;\n';
  output += '\t\tprivate static bool _isDirty = false;\n';
  output += '\t\tprivate static string _storagePath;\n\n';
  output += '\t\t/// <summary>Gets or sets the storage directory path</summary>\n';
  output += '\t\tpublic static string StoragePath\n\t\t{\n';
  output += '\t\t\tget\n\t\t\t{\n';
  output += '\t\t\t\tif (string.IsNullOrEmpty(_storagePath))\n\t\t\t\t{\n';
  output += '\t\t\t\t\tvar appData = Environment.GetFolderPath(Environment.SpecialFolder.LocalApplicationData);\n';
  output += '\t\t\t\t\t_storagePath = Path.Combine(appData, "GrimoireData");\n';
  output += '\t\t\t\t}\n';
  output += '\t\t\t\treturn _storagePath;\n';
  output += '\t\t\t}\n';
  output += '\t\t\tset => _storagePath = value;\n';
  output += '\t\t}\n\n';
  output += '\t\tprivate static string GetFilePath() => Path.Combine(StoragePath, "storage.json");\n\n';
  output += '\t\tprivate static void EnsureLoaded()\n\t\t{\n';
  output += '\t\t\tif (_cache != null) return;\n\n';
  output += '\t\t\t_cache = new Dictionary<string, string>();\n';
  output += '\t\t\tvar filePath = GetFilePath();\n\n';
  output += '\t\t\tif (File.Exists(filePath))\n\t\t\t{\n';
  output += '\t\t\t\ttry\n\t\t\t\t{\n';
  output += '\t\t\t\t\tvar json = File.ReadAllText(filePath);\n';
  output += '\t\t\t\t\t_cache = GrimoireJson.FromJson<Dictionary<string, string>>(json) \n';
  output += '\t\t\t\t\t\t?? new Dictionary<string, string>();\n';
  output += '\t\t\t\t}\n';
  output += '\t\t\t\tcatch (Exception ex)\n\t\t\t\t{\n';
  output += '\t\t\t\t\tGrimoireLog.Error($"Failed to load storage: {ex.Message}");\n';
  output += '\t\t\t\t\t_cache = new Dictionary<string, string>();\n';
  output += '\t\t\t\t}\n';
  output += '\t\t\t}\n';
  output += '\t\t}\n\n';
  output += '\t\tprivate static bool DefaultHasKey(string key)\n\t\t{\n';
  output += '\t\t\tEnsureLoaded();\n';
  output += '\t\t\treturn _cache.ContainsKey(key);\n';
  output += '\t\t}\n\n';
  output += '\t\tprivate static string DefaultGetString(string key)\n\t\t{\n';
  output += '\t\t\tEnsureLoaded();\n';
  output += '\t\t\treturn _cache.TryGetValue(key, out var value) ? value : "";\n';
  output += '\t\t}\n\n';
  output += '\t\tprivate static void DefaultSetString(string key, string value)\n\t\t{\n';
  output += '\t\t\tEnsureLoaded();\n';
  output += '\t\t\t_cache[key] = value;\n';
  output += '\t\t\t_isDirty = true;\n';
  output += '\t\t}\n\n';
  output += '\t\tprivate static void DefaultSave()\n\t\t{\n';
  output += '\t\t\tif (!_isDirty || _cache == null) return;\n\n';
  output += '\t\t\ttry\n\t\t\t{\n';
  output += '\t\t\t\tvar directory = StoragePath;\n';
  output += '\t\t\t\tif (!Directory.Exists(directory))\n\t\t\t\t{\n';
  output += '\t\t\t\t\tDirectory.CreateDirectory(directory);\n';
  output += '\t\t\t\t}\n\n';
  output += '\t\t\t\tvar json = GrimoireJson.ToJson(_cache, true);\n';
  output += '\t\t\t\tFile.WriteAllText(GetFilePath(), json);\n';
  output += '\t\t\t\t_isDirty = false;\n';
  output += '\t\t\t\tGrimoireLog.Info($"Storage saved to: {GetFilePath()}");\n';
  output += '\t\t\t}\n';
  output += '\t\t\tcatch (Exception ex)\n\t\t\t{\n';
  output += '\t\t\t\tGrimoireLog.Error($"Failed to save storage: {ex.Message}");\n';
  output += '\t\t\t}\n';
  output += '\t\t}\n\n';
  output += '\t\tprivate static void DefaultDeleteKey(string key)\n\t\t{\n';
  output += '\t\t\tEnsureLoaded();\n';
  output += '\t\t\tif (_cache.Remove(key))\n\t\t\t{\n';
  output += '\t\t\t\t_isDirty = true;\n';
  output += '\t\t\t}\n';
  output += '\t\t}\n\n';
  output += '\t\tprivate static void DefaultDeleteAll()\n\t\t{\n';
  output += '\t\t\t_cache = new Dictionary<string, string>();\n';
  output += '\t\t\t_isDirty = true;\n';
  output += '\t\t}\n\n';
  output += '\t\t#endregion\n';
  output += '\t}\n';
  output += '}\n';

  return output;
}

/**
 * Generate UnityGrimoireBootstrap.cs - Unity engine adapter
 * Configures the runtime layer to use Unity's Debug and PlayerPrefs
 * JSON is handled by GrimoireJson.cs using Newtonsoft.Json (no configuration needed)
 * @param namespace - C# namespace for the runtime layer (default: 'Grimoire.Data.Runtime')
 */
export function renderUnityBootstrapFile(namespace: string = 'Grimoire.Data.Runtime'): string {
  let output = '// Unity Bootstrap for Grimoire Runtime\n';
  output += '// This file configures the Grimoire runtime layer for Unity.\n';
  output += '// Add this file to your Unity project to enable Unity integration.\n';
  output += '//\n';
  output += '// REQUIRED: Install Newtonsoft.Json via Package Manager:\n';
  output += '//   Window > Package Manager > + > Add package by name: com.unity.nuget.newtonsoft-json\n\n';
  output += '#if UNITY_5_3_OR_NEWER\n';
  output += 'using UnityEngine;\n';
  output += `using ${namespace};\n\n`;
  output += '/// <summary>\n';
  output += '/// Unity bootstrap that configures Grimoire runtime to use Unity\'s APIs.\n';
  output += '/// Automatically initializes before scene load.\n';
  output += '/// \n';
  output += '/// REQUIRED: Install Newtonsoft.Json via Package Manager:\n';
  output += '///   Window > Package Manager > + > Add package by name: com.unity.nuget.newtonsoft-json\n';
  output += '/// </summary>\n';
  output += 'public static class UnityGrimoireBootstrap\n{\n';
  output += '\tprivate static bool _initialized = false;\n\n';
  output += '\t/// <summary>\n';
  output += '\t/// Automatically called before the first scene loads.\n';
  output += '\t/// Configures Grimoire to use Unity\'s logging and storage.\n';
  output += '\t/// JSON serialization uses Newtonsoft.Json (configured in GrimoireJson.cs).\n';
  output += '\t/// </summary>\n';
  output += '\t[RuntimeInitializeOnLoadMethod(RuntimeInitializeLoadType.BeforeSceneLoad)]\n';
  output += '\tpublic static void Initialize()\n\t{\n';
  output += '\t\tif (_initialized) return;\n';
  output += '\t\t_initialized = true;\n\n';
  output += '\t\t// Configure logging to use Unity\'s Debug class\n';
  output += '\t\tGrimoireLog.InfoSink = msg => Debug.Log(msg);\n';
  output += '\t\tGrimoireLog.WarnSink = msg => Debug.LogWarning(msg);\n';
  output += '\t\tGrimoireLog.ErrorSink = msg => Debug.LogError(msg);\n\n';
  output += '\t\t// Configure storage to use Unity\'s PlayerPrefs\n';
  output += '\t\tGrimoireStorage.HasKeyImpl = PlayerPrefs.HasKey;\n';
  output += '\t\tGrimoireStorage.GetStringImpl = key => PlayerPrefs.GetString(key);\n';
  output += '\t\tGrimoireStorage.SetStringImpl = (key, value) => PlayerPrefs.SetString(key, value);\n';
  output += '\t\tGrimoireStorage.SaveImpl = () => PlayerPrefs.Save();\n';
  output += '\t\tGrimoireStorage.DeleteKeyImpl = PlayerPrefs.DeleteKey;\n';
  output += '\t\tGrimoireStorage.DeleteAllImpl = PlayerPrefs.DeleteAll;\n\n';
  output += '\t\t// JSON serialization is handled by GrimoireJson.cs using Newtonsoft.Json\n';
  output += '\t\t// No additional configuration needed here\n\n';
  output += '\t\tDebug.Log("[Grimoire] Unity bootstrap initialized");\n';
  output += '\t}\n\n';
  output += '\t/// <summary>\n';
  output += '\t/// Manually initialize if needed before RuntimeInitializeOnLoadMethod runs.\n';
  output += '\t/// Safe to call multiple times.\n';
  output += '\t/// </summary>\n';
  output += '\tpublic static void EnsureInitialized()\n\t{\n';
  output += '\t\tif (!_initialized)\n\t\t{\n';
  output += '\t\t\tInitialize();\n';
  output += '\t\t}\n';
  output += '\t}\n';
  output += '}\n';
  output += '#endif\n';

  return output;
}

/**
 * Generate GodotGrimoireBootstrap.cs - Godot engine adapter
 * Configures the runtime layer to use Godot's GD, FileAccess, and user:// paths
 * @param namespace - C# namespace for the runtime layer (default: 'Grimoire.Data.Runtime')
 */
export function renderGodotBootstrapFile(namespace: string = 'Grimoire.Data.Runtime'): string {
  let output = '// Godot Bootstrap for Grimoire Runtime\n';
  output += '// This file configures the Grimoire runtime layer for Godot.\n';
  output += '// Add this file to your Godot C# project and call GodotGrimoireBootstrap.Initialize() at startup.\n\n';
  output += '#if GODOT\n';
  output += 'using Godot;\n';
  output += 'using System.Collections.Generic;\n';
  output += `using ${namespace};\n\n`;
  output += '/// <summary>\n';
  output += '/// Godot bootstrap that configures Grimoire runtime to use Godot\'s APIs.\n';
  output += '/// Call Initialize() from your main script\'s _Ready() method.\n';
  output += '/// </summary>\n';
  output += 'public static class GodotGrimoireBootstrap\n{\n';
  output += '\tprivate static bool _initialized = false;\n';
  output += '\tprivate static Dictionary<string, string> _storage;\n';
  output += '\tprivate static string _storagePath;\n\n';
  output += '\t/// <summary>\n';
  output += '\t/// Initialize Grimoire runtime for Godot.\n';
  output += '\t/// Call this from your main script\'s _Ready() method.\n';
  output += '\t/// </summary>\n';
  output += '\tpublic static void Initialize()\n\t{\n';
  output += '\t\tif (_initialized) return;\n';
  output += '\t\t_initialized = true;\n\n';
  output += '\t\t// Set up storage path in Godot user directory\n';
  output += '\t\t_storagePath = ProjectSettings.GlobalizePath("user://grimoire_data/storage.json");\n';
  output += '\t\t_storage = LoadStorage();\n\n';
  output += '\t\t// Configure logging to use Godot\'s output\n';
  output += '\t\tGrimoireLog.InfoSink = msg => GD.Print(msg);\n';
  output += '\t\tGrimoireLog.WarnSink = msg => GD.PushWarning(msg);\n';
  output += '\t\tGrimoireLog.ErrorSink = msg => GD.PushError(msg);\n\n';
  output += '\t\t// Configure storage using Godot file system\n';
  output += '\t\tGrimoireStorage.HasKeyImpl = key => _storage.ContainsKey(key);\n';
  output += '\t\tGrimoireStorage.GetStringImpl = key => _storage.TryGetValue(key, out var val) ? val : "";\n';
  output += '\t\tGrimoireStorage.SetStringImpl = (key, value) => _storage[key] = value;\n';
  output += '\t\tGrimoireStorage.SaveImpl = SaveStorage;\n';
  output += '\t\tGrimoireStorage.DeleteKeyImpl = key => _storage.Remove(key);\n';
  output += '\t\tGrimoireStorage.DeleteAllImpl = () => _storage.Clear();\n\n';
  output += '\t\tGD.Print("[Grimoire] Godot bootstrap initialized");\n';
  output += '\t}\n\n';
  output += '\tprivate static Dictionary<string, string> LoadStorage()\n\t{\n';
  output += '\t\tvar dict = new Dictionary<string, string>();\n';
  output += '\t\tif (!FileAccess.FileExists(_storagePath))\n';
  output += '\t\t\treturn dict;\n\n';
  output += '\t\ttry\n\t\t{\n';
  output += '\t\t\tusing var file = FileAccess.Open(_storagePath, FileAccess.ModeFlags.Read);\n';
  output += '\t\t\tif (file != null)\n\t\t\t{\n';
  output += '\t\t\t\tvar json = file.GetAsText();\n';
  output += '\t\t\t\tdict = GrimoireJson.FromJson<Dictionary<string, string>>(json) \n';
  output += '\t\t\t\t\t?? new Dictionary<string, string>();\n';
  output += '\t\t\t}\n';
  output += '\t\t}\n';
  output += '\t\tcatch (System.Exception ex)\n\t\t{\n';
  output += '\t\t\tGD.PushError($"Failed to load Grimoire storage: {ex.Message}");\n';
  output += '\t\t}\n';
  output += '\t\treturn dict;\n';
  output += '\t}\n\n';
  output += '\tprivate static void SaveStorage()\n\t{\n';
  output += '\t\ttry\n\t\t{\n';
  output += '\t\t\t// Ensure directory exists\n';
  output += '\t\t\tvar dir = _storagePath.GetBaseDir();\n';
  output += '\t\t\tif (!DirAccess.DirExistsAbsolute(dir))\n\t\t\t{\n';
  output += '\t\t\t\tDirAccess.MakeDirRecursiveAbsolute(dir);\n';
  output += '\t\t\t}\n\n';
  output += '\t\t\tusing var file = FileAccess.Open(_storagePath, FileAccess.ModeFlags.Write);\n';
  output += '\t\t\tif (file != null)\n\t\t\t{\n';
  output += '\t\t\t\tvar json = GrimoireJson.ToJson(_storage, true);\n';
  output += '\t\t\t\tfile.StoreString(json);\n';
  output += '\t\t\t}\n';
  output += '\t\t}\n';
  output += '\t\tcatch (System.Exception ex)\n\t\t{\n';
  output += '\t\t\tGD.PushError($"Failed to save Grimoire storage: {ex.Message}");\n';
  output += '\t\t}\n';
  output += '\t}\n';
  output += '}\n';
  output += '#endif\n';

  return output;
}

/**
 * Generate all runtime layer files as a bundle
 * Returns an object with file names and their content
 * @param namespace - C# namespace (default: 'Grimoire.Data.Runtime')
 * @param gameEngine - The game engine type ('unity', 'godot', etc.) - determines which bootstrap file to include
 */
export function renderRuntimeLayerBundle(namespace: string = 'Grimoire.Data.Runtime', gameEngine?: string): Record<string, string> {
  const files: Record<string, string> = {
    'GrimoireLog.cs': renderGrimoireLogFile(namespace),
    'GrimoireJson.cs': renderGrimoireJsonFile(namespace),
    'GrimoireStorage.cs': renderGrimoireStorageFile(namespace),
  };

  // Include the appropriate bootstrap file based on the game engine
  const engine = gameEngine?.toLowerCase();
  if (engine === 'godot') {
    files['GodotGrimoireBootstrap.cs'] = renderGodotBootstrapFile(namespace);
  } else {
    // Default to Unity bootstrap for unity engine or unspecified
    files['UnityGrimoireBootstrap.cs'] = renderUnityBootstrapFile(namespace);
  }

  return files;
}
